/*
  assets/gtender-public-refinements.js — GTENDER-PUBLIC-REFINEMENTS-11
  Owner: additive public GTender display refinements that consume existing search/filter DOM contracts.
  Boundary: no fetch ownership, no pagination ownership, no persistence, no auth, no tender submission.
*/
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-gtender-public-entry]';
  var VERSION = '2026-10-18-public-refinements-16-filter-grabber-geometry';
  var DEFAULT_SEARCH_ENDPOINT = 'https://api.glhksolution.com/api/tender/search';

  var INSTITUTIONS = [
    ['HKUST', '香港科技大學', 'The Hong Kong University of Science and Technology'],
    ['HKMU', '香港都會大學', 'Hong Kong Metropolitan University'],
    [
      'GLD',
      '政府物流服務署',
      'Government Logistics Department',
      'HKSAR GLD',
      '香港特別行政區政府物流服務署',
    ],
    ['ArchSD', '建築署', 'Architectural Services Department', '香港特別行政區建築署'],
    ['AFCD', '漁農自然護理署', 'Agriculture, Fisheries and Conservation Department'],
    ['EPD', '環境保護署', 'Environmental Protection Department'],
    ['EDB', '教育局', 'Education Bureau'],
    ['CSD', '懲教署', 'Correctional Services Department'],
    ['GFS', '政府飛行服務隊', 'Government Flying Service'],
    ['HKO', '香港天文台', 'Hong Kong Observatory'],
    ['WSD', '水務署', 'Water Supplies Department'],
    ['MD', '海事處', 'Marine Department'],
    ['DH', '衞生署', 'Department of Health'],
    ['ImmD', '入境事務處', 'Immigration Department'],
    ['ISD', '政府新聞處', 'Information Services Department'],
    ['HKPF', '香港警務處', 'Hong Kong Police Force'],
    ['HKMA', '香港金融管理局', 'Hong Kong Monetary Authority'],
    ['CEDD', '土木工程拓展署', 'Civil Engineering and Development Department'],
    ['SWD', '社會福利署', 'Social Welfare Department'],
    ['IRD', '稅務局', 'Inland Revenue Department'],
    ['TD', '運輸署', 'Transport Department'],
    ['DEVB (WB)', '發展局（工務科）', 'Development Bureau (Works Branch)'],
    ['HKP', '香港郵政', 'Hongkong Post', 'Hong Kong Post'],
    ['DoJ', '律政司', 'Department of Justice'],
    ['LCSD', '康樂及文化事務署', 'Leisure and Cultural Services Department'],
    ['FEHD', '食物環境衞生署', 'Food and Environmental Hygiene Department'],
    ['DPO', '數字政策辦公室', 'Digital Policy Office'],
    ['JUD', '司法機構', 'Judiciary'],
    ['BD', '屋宇署', 'Buildings Department'],
    [
      'EMSTF',
      '機電工程營運基金',
      'Electrical and Mechanical Services Trading Fund',
      'Electrical & Mechanical Services Trading Fund',
    ],
  ];

  var INSTITUTION_TYPE_OPTIONS = [
    ['government_education', '政府教育', 'Government education'],
    ['university', '大學', 'University'],
    ['vocational_education', '職業教育', 'Vocational education'],
    ['tertiary_college', '專上院校', 'Tertiary college'],
    ['school', '學校', 'School'],
  ];

  const SOURCE_FILTER_OPTIONS = [
    ['gld-etb', '政府物流服務署電子投標', 'GLD e-Tendering'],
    ['hkma', '香港金融管理局', 'Hong Kong Monetary Authority'],
    ['archsd', '建築署', 'Architectural Services Department'],
    ['gpa', '政府產業署', 'Government Property Agency'],
    ['edb', '教育局', 'Education Bureau'],
    ['hkust', '香港科技大學', 'HKUST'],
    ['hkmu', '香港都會大學', 'HKMU'],
    ['hkbu', '香港浸會大學', 'HKBU'],
    ['eduhk', '香港教育大學', 'EdUHK'],
  ];

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function normalized(value) {
    return clean(value)
      .toLowerCase()
      .replace(/^the\s+/, '')
      .replace(/\s+/g, ' ');
  }

  function isZh(root) {
    var locale =
      clean(root && root.getAttribute('data-locale')) ||
      clean(document.documentElement && document.documentElement.lang);
    return /^zh/i.test(locale);
  }

  function ensureResponsiveFilterLayout(root) {
    if (!(root instanceof HTMLElement)) return;

    const form = root.querySelector('[data-gtender-public-search]');
    const sheet = root.querySelector('[data-gtender-filter-sheet]');
    const workbench = root.querySelector('[data-gtender-public-workbench]');
    const listColumn = root.querySelector('[data-gtender-public-list-column]');
    const previewColumn = root.querySelector('[data-gtender-public-preview-column]');

    if (!form || !sheet || !workbench || !listColumn || !previewColumn || !form.parentNode) return;

    const shell = sheet.querySelector('.gtender-filter-sheet__shell');
    if (shell) shell.classList.add('lg-form');

    let layout = root.querySelector('[data-gtender-public-layout]');

    if (!layout) {
      layout = document.createElement('div');
      layout.className = 'gtender-public-layout';
      layout.setAttribute('data-gtender-public-layout', '');
      form.parentNode.insertBefore(layout, form.nextSibling);
    }

    let resultsColumn = layout.querySelector('[data-gtender-public-results-column]');

    if (!resultsColumn) {
      resultsColumn = document.createElement('div');
      resultsColumn.className = 'gtender-public-results-column';
      resultsColumn.setAttribute('data-gtender-public-results-column', '');
    }

    if (sheet.parentElement !== layout) {
      layout.insertBefore(sheet, layout.firstChild);
    }

    if (resultsColumn.parentElement !== layout) {
      layout.appendChild(resultsColumn);
    }

    [
      root.querySelector('[data-gtender-source-health]'),
      root.querySelector('noscript'),
      root.querySelector('[data-gtender-empty-visual-template]'),
      workbench,
    ].forEach((node) => {
      if (node) resultsColumn.appendChild(node);
    });

    const pagination = root.querySelector('.gtender-public-pagination');
    if (pagination) listColumn.appendChild(pagination);

    [
      root.querySelector('[data-gtender-map-workspace]'),
      root.querySelector('[data-gtender-public-detail]'),
    ].forEach((node) => {
      if (node) previewColumn.appendChild(node);
    });
  }

  function optionLabel(root, row) {
    return isZh(root) ? row[1] : row[2];
  }

  function appendSelectField(root, grid, config) {
    if (!grid || root.querySelector(config.selector)) return;
    var field = document.createElement('label');
    field.className = 'gtender-public-search__field';
    field.setAttribute('data-gtender-supported-filter', config.key);

    var label = document.createElement('span');
    var zhHans = /^zh-CN$/i.test(clean(root.getAttribute('data-locale')));
    label.textContent =
      zhHans && config.labelZhHans
        ? config.labelZhHans
        : isZh(root)
          ? config.labelZh
          : config.labelEn;
    field.appendChild(label);

    var select = document.createElement('select');
    select.name = config.name;
    select.setAttribute(config.attribute, '');

    var all = document.createElement('option');
    all.value = '';
    all.textContent = label.textContent;
    select.appendChild(all);

    config.options.forEach(function (row) {
      var option = document.createElement('option');
      option.value = row[0];
      option.textContent = optionLabel(root, row);
      select.appendChild(option);
    });

    field.appendChild(select);
    grid.appendChild(field);
  }

  function ensureSupportedFilters(root) {
    var grid = root.querySelector('.gtender-public-search__filter-grid');
    if (!grid) return;

    appendSelectField(root, grid, {
      key: 'institution-type',
      name: 'institution_type',
      selector: '[data-gtender-search-institution-type]',
      attribute: 'data-gtender-search-institution-type',
      labelZh: '機構類型',
      labelZhHans: '机构类型',
      labelEn: 'Org type',
      options: INSTITUTION_TYPE_OPTIONS,
    });

    appendSelectField(root, grid, {
      key: 'source',
      name: 'source',
      selector: '[data-gtender-search-source]',
      attribute: 'data-gtender-search-source',
      labelZh: '資料來源',
      labelZhHans: '数据来源',
      labelEn: 'Source',
      options: SOURCE_FILTER_OPTIONS,
    });
  }

  function institutionFor(value) {
    var key = normalized(value);
    if (!key) return null;
    for (var index = 0; index < INSTITUTIONS.length; index += 1) {
      var row = INSTITUTIONS[index];
      for (var aliasIndex = 1; aliasIndex < row.length; aliasIndex += 1) {
        if (normalized(row[aliasIndex]) === key) return row;
      }
      if (normalized(row[0]) === key) return row;
    }
    return null;
  }

  function departmentOptionLabel(root, value) {
    var row = institutionFor(value);
    if (!row) return clean(value);
    var full = isZh(root) ? row[1] : row[2];
    return full + (isZh(root) ? '（' : ' (') + row[0] + (isZh(root) ? '）' : ')');
  }

  function normalizeDepartmentOptions(root) {
    var select = root.querySelector('[data-gtender-search-department]');
    if (!select) return;
    Array.prototype.forEach.call(select.options || [], function (option) {
      var value = clean(option.value);
      if (!value) return;
      var label = departmentOptionLabel(root, value);
      if (label && option.textContent !== label) option.textContent = label;
    });
  }

  function bindDepartmentProjection(root) {
    var select = root.querySelector('[data-gtender-search-department]');
    if (!select || select.getAttribute('data-gtender-refinement-department-bound') === 'true')
      return;
    select.setAttribute('data-gtender-refinement-department-bound', 'true');
    normalizeDepartmentOptions(root);
    var observer = new MutationObserver(function () {
      normalizeDepartmentOptions(root);
    });
    observer.observe(select, { childList: true, subtree: true, characterData: true });
    root.__gtenderDepartmentProjectionObserver = observer;
  }

  function searchEndpointBase(root) {
    return clean(root.getAttribute('data-opportunities-endpoint')) || DEFAULT_SEARCH_ENDPOINT;
  }

  function validDateKey(value) {
    var raw = clean(value);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
    var parsed = new Date(raw + 'T00:00:00Z');
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString().slice(0, 10) === raw ? raw : '';
  }

  function applySearchEndpoint(root) {
    var region = clean(root.getAttribute('data-gtender-search-region-value')) || 'hong-kong';
    var publishedFrom = validDateKey(root.getAttribute('data-gtender-published-from'));
    var publishedTo = validDateKey(root.getAttribute('data-gtender-published-to'));
    var institutionControl = root.querySelector('[data-gtender-search-institution-type]');
    var sourceControl = root.querySelector('[data-gtender-search-source]');
    var institutionType = clean(institutionControl && institutionControl.value);
    var source = clean(sourceControl && sourceControl.value);

    try {
      var endpoint = new URL(searchEndpointBase(root), window.location.origin);
      endpoint.searchParams.set('region', region);
      if (publishedFrom && publishedTo) {
        endpoint.searchParams.set('published_from', publishedFrom);
        endpoint.searchParams.set('published_to', publishedTo);
      } else {
        endpoint.searchParams.delete('published_from');
        endpoint.searchParams.delete('published_to');
      }
      if (institutionType) endpoint.searchParams.set('institution_type', institutionType);
      else endpoint.searchParams.delete('institution_type');
      if (source) endpoint.searchParams.set('source', source);
      else endpoint.searchParams.delete('source');
      root.setAttribute('data-tender-search-endpoint', endpoint.toString());
    } catch (_error) {
      var fallback = DEFAULT_SEARCH_ENDPOINT + '?region=' + encodeURIComponent(region);
      if (publishedFrom && publishedTo) {
        fallback +=
          '&published_from=' +
          encodeURIComponent(publishedFrom) +
          '&published_to=' +
          encodeURIComponent(publishedTo);
      }
      if (institutionType) fallback += '&institution_type=' + encodeURIComponent(institutionType);
      if (source) fallback += '&source=' + encodeURIComponent(source);
      root.setAttribute('data-tender-search-endpoint', fallback);
    }
  }

  function applyRegionEndpoint(root, region) {
    root.setAttribute('data-gtender-search-region-value', clean(region) || 'hong-kong');
    applySearchEndpoint(root);
  }

  function bindSupportedFilters(root) {
    var form = root.querySelector('[data-gtender-public-search]');
    if (!form) return;
    ['[data-gtender-search-institution-type]', '[data-gtender-search-source]'].forEach(
      function (selector) {
        var control = root.querySelector(selector);
        if (!control || control.getAttribute('data-gtender-supported-filter-bound') === 'true')
          return;
        control.setAttribute('data-gtender-supported-filter-bound', 'true');
        control.addEventListener('change', function () {
          applySearchEndpoint(root);
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        });
      }
    );
  }

  function pad2(value) {
    return String(value).padStart(2, '0');
  }

  function dateKey(year, month, day) {
    return String(year) + '-' + pad2(month + 1) + '-' + pad2(day);
  }

  function todayKey() {
    var now = new Date();
    try {
      var parts = new Intl.DateTimeFormat('en', {
        timeZone: 'Asia/Hong_Kong',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(now);
      var map = {};
      parts.forEach(function (part) {
        if (part.type !== 'literal') map[part.type] = part.value;
      });
      if (map.year && map.month && map.day) return map.year + '-' + map.month + '-' + map.day;
    } catch (_error) {}
    return dateKey(now.getFullYear(), now.getMonth(), now.getDate());
  }

  function monthTitle(root, year, month) {
    if (isZh(root)) return String(year) + '年' + String(month + 1) + '月';
    return new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' }).format(
      new Date(year, month, 1)
    );
  }

  function rangeLabel(root, start, end) {
    if (start && end) return start + ' ~ ' + end;
    return publishedDateLabel(root);
  }

  function publishedDateLabel(root) {
    return /^zh-CN$/i.test(clean(root.getAttribute('data-locale')))
      ? '发布日期'
      : isZh(root)
        ? '發佈日期'
        : 'Published Date';
  }

  function bindPublishedDateRange(root) {
    var form = root.querySelector('[data-gtender-public-search]');
    var grid = root.querySelector('.gtender-public-search__filter-grid');
    if (!form || !grid || root.querySelector('[data-gtender-published-range]')) return;

    var today = todayKey();
    var todayParts = today.split('-').map(Number);
    var state = {
      start: '',
      end: '',
      viewYear: todayParts[0],
      viewMonth: todayParts[1] - 1,
      selectingEnd: false,
    };

    var field = document.createElement('div');
    field.className = 'gtender-public-search__field gtender-published-range';
    field.setAttribute('data-gtender-published-range', '');
    var label = document.createElement('span');
    label.textContent = publishedDateLabel(root);
    field.appendChild(label);

    var control = document.createElement('div');
    control.className = 'gtender-date-range-control';
    var input = document.createElement('input');
    input.type = 'text';
    input.readOnly = true;
    input.placeholder = rangeLabel(root, '', '');
    input.setAttribute('data-gtender-published-range-input', '');
    input.setAttribute('aria-haspopup', 'dialog');
    input.setAttribute('aria-expanded', 'false');
    control.appendChild(input);

    var clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'gtender-date-range-clear';
    clear.setAttribute('data-gtender-published-range-clear', '');
    clear.setAttribute(
      'aria-label',
      /^zh-CN$/i.test(clean(root.getAttribute('data-locale')))
        ? '清除日期范围'
        : isZh(root)
          ? '清除日期範圍'
          : 'Clear date range'
    );
    clear.textContent = '×';
    clear.hidden = true;
    control.appendChild(clear);
    field.appendChild(control);

    var panel = document.createElement('div');
    panel.className = 'gtender-date-range-panel';
    panel.hidden = true;
    panel.setAttribute('data-gtender-published-range-panel', '');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute(
      'aria-label',
      /^zh-CN$/i.test(clean(root.getAttribute('data-locale')))
        ? '选择日期范围'
        : isZh(root)
          ? '選擇日期範圍'
          : 'Select date range'
    );

    var header = document.createElement('div');
    header.className = 'gtender-date-range-header';
    var prev = document.createElement('button');
    prev.type = 'button';
    prev.setAttribute('data-gtender-date-prev', '');
    prev.setAttribute('aria-label', isZh(root) ? '上個月' : 'Previous month');
    prev.textContent = '‹';
    var title = document.createElement('strong');
    title.setAttribute('data-gtender-date-month-title', '');
    var next = document.createElement('button');
    next.type = 'button';
    next.setAttribute('data-gtender-date-next', '');
    next.setAttribute('aria-label', isZh(root) ? '下個月' : 'Next month');
    next.textContent = '›';
    header.appendChild(prev);
    header.appendChild(title);
    header.appendChild(next);
    panel.appendChild(header);

    var weekdays = document.createElement('div');
    weekdays.className = 'gtender-date-range-weekdays';
    (isZh(root)
      ? ['一', '二', '三', '四', '五', '六', '日']
      : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    ).forEach(function (name) {
      var item = document.createElement('span');
      item.textContent = name;
      weekdays.appendChild(item);
    });
    panel.appendChild(weekdays);

    var calendar = document.createElement('div');
    calendar.className = 'gtender-date-range-calendar';
    calendar.setAttribute('data-gtender-date-calendar', '');
    panel.appendChild(calendar);

    var footer = document.createElement('div');
    footer.className = 'gtender-date-range-footer';
    var todayButton = document.createElement('button');
    todayButton.type = 'button';
    todayButton.setAttribute('data-gtender-date-today', '');
    todayButton.textContent = isZh(root) ? '今天' : 'Today';
    var clearButton = document.createElement('button');
    clearButton.type = 'button';
    clearButton.setAttribute('data-gtender-date-clear', '');
    clearButton.textContent = isZh(root) ? '清空' : 'Clear';
    footer.appendChild(todayButton);
    footer.appendChild(clearButton);
    panel.appendChild(footer);
    field.appendChild(panel);

    grid.insertBefore(field, grid.firstChild);

    function syncInput() {
      const hasRange = Boolean(state.start && state.end);
      const fullRange = hasRange ? rangeLabel(root, state.start, state.end) : '';
      const fieldName = publishedDateLabel(root);

      input.value = fullRange;

      input.title = fullRange || fieldName;
      input.setAttribute('aria-label', fullRange ? `${fieldName}: ${fullRange}` : fieldName);

      /* Keep the beginning visible when updating the focused input. */
      if (document.activeElement === input) {
        input.setSelectionRange(0, 0);
        input.scrollLeft = 0;
      }

      clear.hidden = !(state.start || state.end);
      root.setAttribute('data-gtender-published-from', hasRange ? state.start : '');
      root.setAttribute('data-gtender-published-to', hasRange ? state.end : '');
      applySearchEndpoint(root);
    }

    function inRange(key) {
      return Boolean(state.start && state.end && key >= state.start && key <= state.end);
    }

    function clearRangePreview() {
      calendar.querySelectorAll('[data-gtender-date-day]').forEach((button) => {
        const day = button.getAttribute('data-gtender-date-day');

        button.removeAttribute('data-range-preview');
        button.removeAttribute('data-range-preview-end');
        button.toggleAttribute('data-band-first', Boolean(state.end && day === state.start));
        button.toggleAttribute('data-band-last', Boolean(state.end && day === state.end));
      });
    }

    function previewRange(key) {
      clearRangePreview();

      if (!state.selectingEnd || !state.start || state.end || !key) {
        return;
      }

      const first = key < state.start ? key : state.start;
      const last = key > state.start ? key : state.start;

      calendar.querySelectorAll('[data-gtender-date-day]:not([disabled])').forEach((button) => {
        const day = button.getAttribute('data-gtender-date-day');

        button.toggleAttribute('data-band-first', day === first);
        button.toggleAttribute('data-band-last', day === last);

        if (day >= first && day <= last) {
          button.setAttribute('data-range-preview', 'true');
        }

        if (day === key) {
          button.setAttribute('data-range-preview-end', 'true');
        }
      });
    }

    function renderCalendar() {
      title.textContent = monthTitle(root, state.viewYear, state.viewMonth);
      calendar.textContent = '';
      var firstWeekday = new Date(state.viewYear, state.viewMonth, 1).getDay();
      var firstOffset = (firstWeekday + 6) % 7;
      var days = new Date(state.viewYear, state.viewMonth + 1, 0).getDate();
      var totalCells = Math.ceil((firstOffset + days) / 7) * 7;
      for (var cell = 0; cell < totalCells; cell += 1) {
        var cellDate = new Date(state.viewYear, state.viewMonth, cell - firstOffset + 1);
        var key = dateKey(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
        var outside = cellDate.getMonth() !== state.viewMonth;
        var dayButton = document.createElement('button');
        dayButton.type = 'button';
        dayButton.textContent = String(cellDate.getDate());
        dayButton.setAttribute('data-gtender-date-day', key);
        dayButton.setAttribute('aria-label', key);
        if (outside) {
          dayButton.setAttribute('data-outside-month', 'true');
          dayButton.disabled = true;
        }
        if (key === today) dayButton.setAttribute('data-today', 'true');
        if (key === state.start) dayButton.setAttribute('data-range-start', 'true');
        if (key === state.end) dayButton.setAttribute('data-range-end', 'true');
        if (inRange(key)) dayButton.setAttribute('data-in-range', 'true');
        if (state.end && key === state.start) dayButton.setAttribute('data-band-first', '');
        if (state.end && key === state.end) dayButton.setAttribute('data-band-last', '');
        calendar.appendChild(dayButton);
      }
    }

    function openPanel() {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      renderCalendar();
    }
    function closePanel(returnFocus) {
      if (panel.hidden) return;
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      if (returnFocus) input.focus();
    }
    function commitRange(submit) {
      syncInput();
      renderCalendar();
      if (submit) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
    function resetRange(submit) {
      state.start = '';
      state.end = '';
      state.selectingEnd = false;
      commitRange(Boolean(submit));
    }

    input.addEventListener('click', function () {
      if (panel.hidden) openPanel();
      else closePanel(false);
    });
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        openPanel();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closePanel(false);
      }
    });
    calendar.addEventListener('pointerover', (event) => {
      if (event.pointerType === 'touch') return;

      const button = event.target.closest('[data-gtender-date-day]:not([disabled])');

      if (!button || !calendar.contains(button)) return;

      previewRange(validDateKey(button.getAttribute('data-gtender-date-day')));
    });

    calendar.addEventListener('pointerleave', clearRangePreview);

    calendar.addEventListener('focusin', (event) => {
      const button = event.target.closest('[data-gtender-date-day]:not([disabled])');

      if (!button || !calendar.contains(button)) return;

      previewRange(validDateKey(button.getAttribute('data-gtender-date-day')));
    });

    calendar.addEventListener('focusout', (event) => {
      if (!calendar.contains(event.relatedTarget)) {
        clearRangePreview();
      }
    });

    prev.addEventListener('click', function () {
      state.viewMonth -= 1;
      if (state.viewMonth < 0) {
        state.viewMonth = 11;
        state.viewYear -= 1;
      }
      renderCalendar();
    });
    next.addEventListener('click', function () {
      state.viewMonth += 1;
      if (state.viewMonth > 11) {
        state.viewMonth = 0;
        state.viewYear += 1;
      }
      renderCalendar();
    });
    calendar.addEventListener('click', function (event) {
      var selectedDay =
        event.target && event.target.closest
          ? event.target.closest('[data-gtender-date-day]:not([disabled])')
          : null;
      if (!selectedDay) return;
      var key = validDateKey(selectedDay.getAttribute('data-gtender-date-day'));
      if (!key) return;
      if (!state.start || (state.start && state.end) || !state.selectingEnd) {
        state.start = key;
        state.end = '';
        state.selectingEnd = true;
        renderCalendar();
        return;
      }
      if (key < state.start) {
        state.end = state.start;
        state.start = key;
      } else state.end = key;
      state.selectingEnd = false;
      commitRange(true);
      closePanel(true);
    });
    todayButton.addEventListener('click', function () {
      state.start = today;
      state.end = today;
      state.selectingEnd = false;
      state.viewYear = todayParts[0];
      state.viewMonth = todayParts[1] - 1;
      commitRange(true);
      closePanel(true);
    });
    function clearAll(event) {
      if (event) event.preventDefault();
      resetRange(true);
      closePanel(true);
    }
    clear.addEventListener('click', clearAll);
    clearButton.addEventListener('click', clearAll);
    document.addEventListener('pointerdown', function (event) {
      if (panel.hidden || field.contains(event.target)) return;
      closePanel(false);
    });
    root.addEventListener(
      'click',
      function (event) {
        var target =
          event.target && event.target.closest
            ? event.target.closest('[data-gtender-search-clear], .gtender-public-empty__clear')
            : null;
        if (!target) return;
        state.start = '';
        state.end = '';
        state.selectingEnd = false;
        var institutionControl = root.querySelector('[data-gtender-search-institution-type]');
        var sourceControl = root.querySelector('[data-gtender-search-source]');
        if (institutionControl) institutionControl.value = '';
        if (sourceControl) sourceControl.value = '';
        syncInput();
        closePanel(false);
      },
      true
    );

    syncInput();
    renderCalendar();
  }

  function regionOptions(root) {
    var isHans = /^zh-CN$/i.test(clean(root.getAttribute('data-locale')));
    return isHans
      ? [
          { value: 'hong-kong', label: '香港' },
          { value: 'mainland-china', label: '中国内地' },
        ]
      : isZh(root)
        ? [
            { value: 'hong-kong', label: '香港' },
            { value: 'mainland-china', label: '中國內地' },
          ]
        : [
            { value: 'hong-kong', label: 'Hong Kong' },
            { value: 'mainland-china', label: 'Mainland China' },
          ];
  }

  function bindRegionSelector(root) {
    var field = root.querySelector('.gtender-public-search__field--query');
    var form = root.querySelector('[data-gtender-public-search]');
    if (!field || !form || root.querySelector('[data-gtender-search-region]')) return;
    var options = regionOptions(root);
    var regionA11yName = /^zh-CN$/i.test(clean(root.getAttribute('data-locale')))
      ? '搜索地区'
      : isZh(root)
        ? '搜尋地區'
        : 'Search region';
    var menuId = (root.id || 'gtender-public') + '-region-menu';
    var wrap = document.createElement('div');
    wrap.className = 'gtender-search-location';
    wrap.setAttribute('data-gtender-search-region-wrap', '');
    var trigger = document.createElement('button');
    trigger.className = 'gtender-search-location__trigger';
    trigger.type = 'button';
    trigger.value = 'hong-kong';
    trigger.setAttribute('data-gtender-search-region', '');
    trigger.setAttribute(
      'aria-label',
      regionA11yName + (isZh(root) ? '：' : ': ') + options[0].label
    );
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', menuId);
    var triggerLabel = document.createElement('span');
    triggerLabel.setAttribute('data-gtender-search-region-label', '');
    var triggerChevron = document.createElement('span');
    triggerChevron.className = 'gtender-search-location__chevron';
    triggerChevron.setAttribute('aria-hidden', 'true');
    triggerChevron.textContent = '⌄';
    trigger.appendChild(triggerLabel);
    trigger.appendChild(triggerChevron);
    wrap.appendChild(trigger);
    var committedValue = 'hong-kong';
    var menu = document.createElement('div');
    menu.className = 'gtender-search-location-menu';
    menu.id = menuId;
    menu.hidden = true;
    menu.setAttribute('data-gtender-search-region-menu', '');
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', regionA11yName);
    options.forEach(function (entry) {
      var option = document.createElement('button');
      option.className = 'gtender-search-location-menu__option';
      option.type = 'button';
      option.value = entry.value;
      option.setAttribute('data-gtender-search-region-option', entry.value);
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.textContent = entry.label;
      menu.appendChild(option);
    });
    field.appendChild(wrap);
    field.appendChild(menu);

    function entryFor(value) {
      return (
        options.find(function (entry) {
          return entry.value === value;
        }) || options[0]
      );
    }
    function optionButtons() {
      return Array.prototype.slice.call(
        menu.querySelectorAll('[data-gtender-search-region-option]')
      );
    }
    function sync() {
      var entry = entryFor(trigger.value);
      trigger.value = entry.value;
      triggerLabel.textContent = entry.label;
      trigger.setAttribute('aria-label', regionA11yName + (isZh(root) ? '：' : ': ') + entry.label);
      optionButtons().forEach(function (option) {
        option.setAttribute('aria-selected', option.value === entry.value ? 'true' : 'false');
      });
    }
    function closeMenu(returnFocus) {
      if (menu.hidden) return;
      menu.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      field.removeAttribute('data-gtender-region-open');
      if (returnFocus) trigger.focus();
    }
    function openMenu(focusIndex) {
      if (!menu.hidden) return;
      menu.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      field.setAttribute('data-gtender-region-open', 'true');
      var buttons = optionButtons();
      var selectedIndex = buttons.findIndex(function (option) {
        return option.value === trigger.value;
      });
      var index = typeof focusIndex === 'number' ? focusIndex : selectedIndex;
      if (index < 0) index = 0;
      if (buttons[index]) buttons[index].focus();
    }
    function commitRegion(value, returnFocus) {
      var next = entryFor(value).value;
      var changed = next !== committedValue;
      trigger.value = next;
      committedValue = next;
      sync();
      applyRegionEndpoint(root, trigger.value);
      closeMenu(returnFocus);
      if (changed) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
    trigger.addEventListener('click', function () {
      if (menu.hidden) openMenu();
      else closeMenu(false);
    });
    trigger.addEventListener('change', function () {
      commitRegion(trigger.value, false);
    });
    trigger.addEventListener('keydown', function (event) {
      if (
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'Enter' ||
        event.key === ' '
      ) {
        event.preventDefault();
        if (menu.hidden) openMenu(event.key === 'ArrowUp' ? optionButtons().length - 1 : undefined);
        else if (event.key === 'Enter' || event.key === ' ') closeMenu(false);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu(false);
      }
    });
    menu.addEventListener('click', function (event) {
      var option =
        event.target && event.target.closest
          ? event.target.closest('[data-gtender-search-region-option]')
          : null;
      if (!option) return;
      commitRegion(option.value, true);
    });
    menu.addEventListener('keydown', function (event) {
      var buttons = optionButtons();
      var current = document.activeElement;
      var index = buttons.indexOf(current);
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu(true);
        return;
      }
      if (event.key === 'Tab') {
        closeMenu(false);
        return;
      }
      if (
        event.key !== 'ArrowDown' &&
        event.key !== 'ArrowUp' &&
        event.key !== 'Home' &&
        event.key !== 'End'
      )
        return;
      event.preventDefault();
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = buttons.length - 1;
      else if (event.key === 'ArrowDown') index = (index + 1 + buttons.length) % buttons.length;
      else index = (index - 1 + buttons.length) % buttons.length;
      if (buttons[index]) buttons[index].focus();
    });
    var query = field.querySelector('[data-gtender-search-query]');
    if (query)
      query.addEventListener('focus', function () {
        closeMenu(false);
      });
    document.addEventListener('pointerdown', function (event) {
      if (menu.hidden || field.contains(event.target)) return;
      closeMenu(false);
    });
    sync();
    applyRegionEndpoint(root, trigger.value);
  }

  function bindCompactFilterPresentation(root) {
    const grid = root.querySelector('.gtender-public-search__filter-grid');
    const template = root.querySelector('[data-gtender-filter-icons]');

    if (!grid || !template || grid.hasAttribute('data-compact-bound')) return;
    grid.setAttribute('data-compact-bound', '');

    const fields = [
      ['category', 'grid'],
      ['status', 'checklist'],
      ['department', 'town-hall'],
      ['deadline', 'clock'],
      ['institution-type', 'building'],
      ['source', 'records'],
    ];

    const sourceCodes = {
      'gld-etb': 'GLD',
      hkma: 'HKMA',
      archsd: 'ArchSD',
      gpa: 'GPA',
      edb: 'EDB',
      hkust: 'HKUST',
      hkmu: 'HKMU',
      hkbu: 'HKBU',
      eduhk: 'EdUHK',
    };

    const locale = clean(root.getAttribute('data-locale')) || document.documentElement.lang || 'en';

    let dayFormatter;
    try {
      dayFormatter = new Intl.NumberFormat(locale, {
        style: 'unit',
        unit: 'day',
        unitDisplay: 'narrow',
      });
    } catch (_) {
      dayFormatter = null;
    }

    function iconFor(name) {
      const original = template.content.querySelector('.icon-' + name);
      if (!original) return null;

      const icon = original.cloneNode(true);
      icon.classList.add('gtender-filter-icon');
      icon.setAttribute('aria-hidden', 'true');
      icon.setAttribute('focusable', 'false');
      return icon;
    }

    function shortLabel(key, select, full) {
      if (!select.value) return full;

      if (key === 'department') {
        const institution =
          institutionFor(select.value) ||
          INSTITUTIONS.find((row) => full === departmentOptionLabel(root, row[2]));
        return institution ? institution[0] : full;
      }

      if (key === 'source') {
        return sourceCodes[select.value] || full;
      }

      if (key === 'institution-type') {
        const labels = {
          government_education: template.dataset.governmentEducation,
          vocational_education: template.dataset.vocationalEducation,
          tertiary_college: template.dataset.tertiaryCollege,
        };
        return labels[select.value] || full;
      }

      if (key === 'category') {
        const value = clean(select.value)
          .toLowerCase()
          .replace(/[\s_]+/g, '-');

        if (value === 'other-government-services') {
          return template.dataset.governmentServices || full;
        }
        if (value === 'digital-it') return 'IT';
      }

      if (key === 'deadline' && dayFormatter) {
        if (select.value === '7') {
          return '≤ ' + dayFormatter.format(7);
        }
        if (select.value === '30') {
          return '≤ ' + dayFormatter.format(30);
        }
        if (select.value === 'later') {
          return '> ' + dayFormatter.format(30);
        }
      }

      return full;
    }

    function sync() {
      fields.forEach(([key, iconName]) => {
        const select = grid.querySelector('[data-gtender-search-' + key + ']');
        const field = select && select.closest('.gtender-public-search__field');
        if (!field) return;

        if (!field.hasAttribute('data-compact-filter')) {
          const icon = iconFor(iconName);
          if (!icon) return;

          field.insertBefore(icon, select);

          const value = document.createElement('i');
          value.className = 'gtender-filter-value';
          value.setAttribute('aria-hidden', 'true');
          field.appendChild(value);
          field.setAttribute('data-compact-filter', '');
        }

        const option = select.selectedOptions[0];
        const full = option ? clean(option.textContent) : '';
        const value = field.querySelector('.gtender-filter-value');
        const short = shortLabel(key, select, full);

        if (value.textContent !== short) value.textContent = short;
        field.title = full;
        select.title = full;
      });

      const input = grid.querySelector('[data-gtender-published-range-input]');
      const control = input && input.closest('.gtender-date-range-control');

      if (control && !control.querySelector('.gtender-filter-icon')) {
        const icon = iconFor('calendar');
        if (icon) control.insertBefore(icon, input);
      }
    }

    let scheduled = false;

    function scheduleSync() {
      if (scheduled) return;
      scheduled = true;

      queueMicrotask(() => {
        scheduled = false;
        sync();
      });
    }

    root.addEventListener('change', scheduleSync);

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-gtender-search-clear], .gtender-public-empty__clear')) {
        scheduleSync();
      }
    });

    const form = root.querySelector('[data-gtender-public-search]');
    if (form) form.addEventListener('reset', scheduleSync);

    const observer = new MutationObserver(scheduleSync);
    observer.observe(grid, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['selected'],
    });

    sync();
  }

  function mount(root) {
    if (!root || root.getAttribute('data-gtender-public-refinements-mounted') === VERSION) return;
    root.setAttribute('data-gtender-public-refinements-mounted', VERSION);

    ensureResponsiveFilterLayout(root);
    ensureSupportedFilters(root);
    bindDepartmentProjection(root);
    bindPublishedDateRange(root);
    bindSupportedFilters(root);
    bindRegionSelector(root);
    bindCompactFilterPresentation(root);
  }

  function boot(scope) {
    const host = scope && scope.querySelectorAll ? scope : document;

    if (host.matches && host.matches(ROOT_SELECTOR)) {
      mount(host);
    }

    Array.prototype.forEach.call(host.querySelectorAll(ROOT_SELECTOR), mount);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });

  window.__GTENDER_PUBLIC_REFINEMENTS__ = Object.freeze({
    version: VERSION,
    boot: boot,
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        boot(document);
      },
      { once: true }
    );
  } else {
    boot(document);
  }
})();
